// src/components/Presentation/PresentationCreator.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Presentation, 
  ArrowRight, 
  Upload, 
  FileText, 
  Palette,
  Settings,
  Users,
  Play,
  Monitor,
  Tv
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  createPresentation, 
} from '../../services/presentationAPI';
import toast from 'react-hot-toast';

const PresentationCreator = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [presentationData, setPresentationData] = useState({
    title: '',
    description: '',
    template: 'blank',
    theme: 'modern',
    aspectRatio: '16:9', // New aspect ratio field
    dimensions: { width: 1920, height: 1080 }, // New dimensions field
    settings: {
      transition: 'slide',
      autoPlay: false,
      showNavigation: true,
      allowQuestions: true,
      enableChat: false
    }
  });

  // Aspect ratio options
  const aspectRatios = [
    {
      id: '16:9',
      name: '16:9 Widescreen',
      description: 'Modern HD displays, laptops',
      dimensions: { width: 1920, height: 1080 },
      preview: 'aspect-[16/9]',
      icon: Monitor
    },
    {
      id: '4:3',
      name: '4:3 Standard',
      description: 'Traditional projectors, older displays',
      dimensions: { width: 1024, height: 768 },
      preview: 'aspect-[4/3]',
      icon: Tv
    }
  ];

  const handleAspectRatioChange = (aspectRatio) => {
    const selectedRatio = aspectRatios.find(ar => ar.id === aspectRatio);
    setPresentationData(prev => ({
      ...prev,
      aspectRatio: aspectRatio,
      dimensions: selectedRatio ? selectedRatio.dimensions : prev.dimensions
    }));
  };

  const templates = [
    {
      id: 'blank',
      name: 'Blank Presentation',
      description: 'Start with a clean slate',
      preview: 'bg-gradient-to-br from-gray-100 to-gray-200',
      slides: 1
    },
    {
      id: 'business',
      name: 'Business Pitch',
      description: 'Professional business presentation',
      preview: 'bg-gradient-to-br from-blue-500 to-indigo-600',
      slides: 8
    },
    {
      id: 'education',
      name: 'Educational',
      description: 'Interactive learning presentation',
      preview: 'bg-gradient-to-br from-green-500 to-teal-600',
      slides: 12
    },
    {
      id: 'marketing',
      name: 'Marketing',
      description: 'Product or service showcase',
      preview: 'bg-gradient-to-br from-purple-500 to-pink-600',
      slides: 10
    },
    {
      id: 'workshop',
      name: 'Workshop',
      description: 'Interactive workshop with polls',
      preview: 'bg-gradient-to-br from-orange-500 to-red-600',
      slides: 15
    }
  ];

  const themes = [
    { id: 'modern', name: 'Modern', preview: 'bg-gradient-to-br from-blue-500 to-purple-600' },
    { id: 'corporate', name: 'Corporate', preview: 'bg-gradient-to-br from-gray-600 to-gray-800' },
    { id: 'creative', name: 'Creative', preview: 'bg-gradient-to-br from-pink-500 to-violet-600' },
    { id: 'minimal', name: 'Minimal', preview: 'bg-gradient-to-br from-gray-400 to-gray-600' },
    { id: 'vibrant', name: 'Vibrant', preview: 'bg-gradient-to-br from-orange-500 to-red-600' }
  ];

  const handleCreatePresentation = async () => {
    if (!presentationData.title.trim()) {
      toast.error('Please enter a presentation title');
      return;
    }

    setIsLoading(true);
    try {
      // Fix: Use the imported function with alias
      const presentation = await createPresentation(presentationData, user.uid);
      toast.success('Presentation created successfully!');
      navigate(`/presentation/${presentation.id}/edit`);
    } catch (error) {
      toast.error('Error creating presentation: ' + error.message);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPresentationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSettingChange = (setting, value) => {
    setPresentationData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [setting]: value
      }
    }));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <Presentation className="w-16 h-16 mx-auto text-indigo-600 mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Interactive Presentation</h1>
        <p className="text-gray-600">Build engaging presentations with interactive elements and real-time audience participation</p>
      </div>

      {/* Progress Steps */}
      <div className="flex justify-center">
        <div className="flex items-center space-x-4">
          <div className={`flex items-center space-x-2 ${step >= 1 ? 'text-indigo-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
              1
            </div>
            <span className="text-sm font-medium">Template</span>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400" />
          <div className={`flex items-center space-x-2 ${step >= 2 ? 'text-indigo-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
              2
            </div>
            <span className="text-sm font-medium">Customize</span>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400" />
          <div className={`flex items-center space-x-2 ${step >= 3 ? 'text-indigo-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
              3
            </div>
            <span className="text-sm font-medium">Create</span>
          </div>
        </div>
      </div>

      {/* Step 1: Choose Template */}
      {step === 1 && (
        <div className="bg-white rounded-lg shadow-sm border p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Choose a Template</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <div
                key={template.id}
                onClick={() => {
                  handleInputChange('template', template.id);
                  setStep(2);
                }}
                className={`
                  relative p-6 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-lg
                  ${presentationData.template === template.id
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
              >
                <div className={`w-full h-32 rounded-lg mb-4 ${template.preview} flex items-center justify-center`}>
                  <Presentation className="w-12 h-12 text-white opacity-80" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{template.name}</h3>
                <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                <div className="flex items-center text-xs text-gray-500">
                  <FileText className="w-3 h-3 mr-1" />
                  {template.slides} slides
                </div>
                {presentationData.template === template.id && (
                  <div className="absolute top-3 right-3 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Customize */}
      {step === 2 && (
        <div className="bg-white rounded-lg shadow-sm border p-8 space-y-8">
          {/* Basic Information */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Customize Your Presentation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Presentation Title *
                </label>
                <input
                  type="text"
                  value={presentationData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter presentation title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template
                </label>
                <div className="flex items-center space-x-3 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                  <Presentation className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{templates.find(t => t.id === presentationData.template)?.name}</span>
                  <button
                    onClick={() => setStep(1)}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={presentationData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                rows={3}
                placeholder="Enter a description for your presentation"
              />
            </div>
          </div>

          {/* NEW: Aspect Ratio Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Monitor className="w-5 h-5 mr-2" />
              Aspect Ratio
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {aspectRatios.map((ratio) => {
                const IconComponent = ratio.icon;
                return (
                  <div
                    key={ratio.id}
                    onClick={() => handleAspectRatioChange(ratio.id)}
                    className={`
                      relative p-6 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md
                      ${presentationData.aspectRatio === ratio.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    {/* Aspect Ratio Preview */}
                    <div className="flex items-center justify-center mb-4">
                      <div className={`bg-gray-200 rounded-lg ${ratio.preview} w-24 h-auto flex items-center justify-center`}>
                        <IconComponent className="w-8 h-8 text-gray-500" />
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <h4 className="font-semibold text-gray-900 mb-1">{ratio.name}</h4>
                      <p className="text-sm text-gray-600 mb-2">{ratio.description}</p>
                      <p className="text-xs text-gray-500">
                        {ratio.dimensions.width} × {ratio.dimensions.height}
                      </p>
                    </div>

                    {presentationData.aspectRatio === ratio.id && (
                      <div className="absolute top-3 right-3 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Theme Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Palette className="w-5 h-5 mr-2" />
              Choose Theme
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {themes.map((theme) => (
                <div
                  key={theme.id}
                  onClick={() => handleInputChange('theme', theme.id)}
                  className={`
                    relative p-4 border-2 rounded-lg cursor-pointer transition-colors
                    ${presentationData.theme === theme.id
                      ? 'border-indigo-500'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <div className={`w-full h-16 rounded-lg mb-3 ${theme.preview}`}></div>
                  <p className="text-sm font-medium text-center">{theme.name}</p>
                  {presentationData.theme === theme.id && (
                    <div className="absolute top-2 right-2 w-4 h-4 bg-indigo-600 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Interactive Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Interactive Features
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Allow Questions</label>
                    <p className="text-xs text-gray-500">Let audience ask questions</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={presentationData.settings.allowQuestions}
                      onChange={(e) => handleSettingChange('allowQuestions', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Show Navigation</label>
                    <p className="text-xs text-gray-500">Display slide navigation</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={presentationData.settings.showNavigation}
                      onChange={(e) => handleSettingChange('showNavigation', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Enable Chat</label>
                    <p className="text-xs text-gray-500">Live chat with audience</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={presentationData.settings.enableChat}
                      onChange={(e) => handleSettingChange('enableChat', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Auto Play</label>
                    <p className="text-xs text-gray-500">Automatically advance slides</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={presentationData.settings.autoPlay}
                      onChange={(e) => handleSettingChange('autoPlay', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Transition Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Slide Transitions
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['slide', 'fade', 'zoom', 'flip'].map((transition) => (
                <div
                  key={transition}
                  onClick={() => handleSettingChange('transition', transition)}
                  className={`
                    p-4 border-2 rounded-lg cursor-pointer transition-colors capitalize
                    ${presentationData.settings.transition === transition
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <div className="text-center">
                    <Play className="w-8 h-8 mx-auto mb-2 text-gray-600" />
                    <span className="font-medium">{transition}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <button
              onClick={() => setStep(1)}
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleCreatePresentation} // Fix: Use the renamed function
              disabled={isLoading || !presentationData.title.trim()}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Presentation className="w-4 h-4 mr-2" />
                  Create Presentation
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresentationCreator;